import type { Move, Piece, Position } from '../stores/gameStore'
import { generateBoardPositions } from '../utils/board'

// Client-side legal-move generation, mirroring the backend MoveGenerator:
// single steps to an empty neighbour, or chains of jumps over an adjacent piece
// into the empty cell beyond. Used only for AI look-ahead — the move actually
// played is always chosen from the backend-authoritative legal-move list, so
// small rule differences here can only affect evaluation, never legality.

const DIRS: Position[] = [
  { q: 1, r: 0 },
  { q: -1, r: 0 },
  { q: 0, r: 1 },
  { q: 0, r: -1 },
  { q: 1, r: -1 },
  { q: -1, r: 1 },
]

const BOARD = new Set(generateBoardPositions().map((p) => `${p.q},${p.r}`))
const key = (q: number, r: number) => `${q},${r}`
const onBoard = (q: number, r: number) => BOARD.has(key(q, r))

// Optional rule (used by the real 2-player game, not the landing animation): a
// marble that has reached its target home may not leave it — mirrors the
// backend MoveGenerator's goal-zone lock.
export interface GoalZoneConstraint {
  goalZone: Set<string> // the player's destination home
  homeZone: Set<string> // the player's own starting home
}

export function generateMoves(
  pieces: Piece[],
  player: number,
  constraint?: GoalZoneConstraint,
): Move[] {
  const occupied = new Set<string>()
  for (const p of pieces) occupied.add(key(p.position.q, p.position.r))

  const moves: Move[] = []
  for (const piece of pieces) {
    if (piece.player !== player) continue
    const { q, r } = piece.position

    // A piece sitting in the goal zone (and not its own home) is locked in — it
    // may only move to cells that are still inside the goal zone.
    const startKey = key(q, r)
    const locked = constraint
      ? constraint.goalZone.has(startKey) && !constraint.homeZone.has(startKey)
      : false
    const allowed = (k: string) => !locked || (constraint as GoalZoneConstraint).goalZone.has(k)

    // Single steps into an empty adjacent cell
    for (const d of DIRS) {
      const nq = q + d.q
      const nr = r + d.r
      const nKey = key(nq, nr)
      if (onBoard(nq, nr) && !occupied.has(nKey) && allowed(nKey)) {
        moves.push({ start: { q, r }, end: { q: nq, r: nr }, path: [{ q, r }, { q: nq, r: nr }] })
      }
    }

    // Jump chains (over an adjacent piece to the empty cell beyond), no revisits
    const visited = new Set<string>([startKey])
    const explore = (pos: Position, path: Position[]) => {
      for (const d of DIRS) {
        const midq = pos.q + d.q
        const midr = pos.r + d.r
        const landq = pos.q + 2 * d.q
        const landr = pos.r + 2 * d.r
        const landKey = key(landq, landr)
        if (
          occupied.has(key(midq, midr)) &&
          onBoard(landq, landr) &&
          !occupied.has(landKey) &&
          !visited.has(landKey) &&
          allowed(landKey)
        ) {
          visited.add(landKey)
          const newPath = [...path, { q: landq, r: landr }]
          moves.push({ start: { q, r }, end: { q: landq, r: landr }, path: newPath })
          explore({ q: landq, r: landr }, newPath)
        }
      }
    }
    explore({ q, r }, [{ q, r }])
  }
  return moves
}
